import { useState } from 'react'

import { IconX } from '@tabler/icons-react'

import type { Expense, Income } from '@subaio/api-types/budget'
import { ButtonGroup, Collapse, FormattedMessage } from '@subaio/components'
import { Form } from '@subaio/components/src/HookForm'

import type { ServiceType } from 'enums'

import { ButtonSolidSubmit } from 'components/Buttons'
import { Select } from 'components/FormFields'

import { Transaction } from '../../Transaction'
import { ButtonLink } from '../ButtonLink'
import { UncategorizedService } from '../UncategorizedService'
import { ButtonClose } from './ButtonClose'
import { Container } from './Container'
import { Title } from './Title'

const defaultVisibleTransactions = 4

type Model = {
	subCategoryId: string
}

type Props = {
	type: ServiceType
	service: Expense | Income
	currency: string
	categoriesMap: Array<{
		label: string
		value: {
			id: string
			subCategoryId: string
		}
	}>

	onRecategorize: (args: { categoryId: string; subCategoryId: string; serviceId: string }) => Promise<void>
	onClose: () => void
}

export const UncategorizeForm = ({ type, service, currency, categoriesMap, onRecategorize, onClose }: Props) => {
	const [showAllTransactions, setShowAllTransactions] = useState(false)

	return (
		<Container>
			<Title>
				<FormattedMessage
					id='flow.budgetEditForm.uncategorized.form.title'
					defaultMessage='Select the category that best covers the {type, select, expense {expense} other {income}}'
					values={{ type }}
				/>
				<ButtonClose onClick={() => onClose()}>
					<IconX />
				</ButtonClose>
			</Title>
			<Form<Model>
				render={(_, { formState }) => (
					<>
						<div>
							<Select
								options={categoriesMap.map(({ label, value }) => ({ label, value: value.subCategoryId }))}
								name='subCategoryId'
								label={
									<FormattedMessage
										id='flow.budgetEditForm.uncategorized.form.labelRecateogrize'
										defaultMessage='Select a category'
									/>
								}
								isClearable={false}
								maxNumberOfOptionsVisible={100}
								isRequired
							/>
							<UncategorizedService
								key={service.id}
								amountToShow='default'
								{...service}
								currency={currency}
								onClick={undefined}
							/>
						</div>
						<Collapse>
							{service.transactions
								.slice(0, showAllTransactions ? service.transactions.length : defaultVisibleTransactions)
								.map((transaction, index) => (
									<Transaction isLeftPadded={false} {...transaction} key={index} />
								))}
						</Collapse>
						{service.transactions.length > defaultVisibleTransactions && (
							<ButtonGroup center>
								<ButtonLink
									label={
										showAllTransactions ? (
											<FormattedMessage
												id='flow.budgetEditForm.uncategorized.labelShowLess'
												defaultMessage='Show less'
											/>
										) : (
											<FormattedMessage
												id='flow.budgetEditForm.uncategorized.labelShowMore'
												defaultMessage='Show more'
											/>
										)
									}
									onClick={() => setShowAllTransactions((showAllTransactions) => !showAllTransactions)}
								/>
							</ButtonGroup>
						)}
						<ButtonGroup spacing={12} right>
							<ButtonSolidSubmit
								label={
									<FormattedMessage
										id='flow.budgetEditForm.uncategorized.form.labelContinue'
										defaultMessage='Continue'
									/>
								}
								disabled={formState.isSubmitting}
								isLoading={formState.isSubmitting}
							/>
						</ButtonGroup>
					</>
				)}
				onSubmit={async ({ subCategoryId }) => {
					const category = categoriesMap.find(({ value }) => value.subCategoryId === subCategoryId)?.value
					if (!category) throw new Error('Could not find a matching category')
					await onRecategorize({
						categoryId: category.id,
						subCategoryId: category.subCategoryId,
						serviceId: service.id,
					})
				}}
			/>
		</Container>
	)
}
